import { useNavigate } from "react-router-dom";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { GripVertical, ChevronUp, ChevronDown, FileText, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusDot } from "@/components/common";
import { CANDIDATE_STATE } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { useEvaluation } from "./EvaluatorApp";

function CandidateCard({ c, index, sortable, total, onMove }) {
  const { token, ctx } = useEvaluation();
  const overall = ctx.evaluations[c.id]?.overall_score;
  const nav = useNavigate();
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: c.id, disabled: !sortable });
  const style = { transform: CSS.Transform.toString(transform), transition: transition || "transform 180ms cubic-bezier(0.2,0.8,0.2,1)" };
  const open = () => nav(`/e/${token}/candidates/${c.id}`);
  return (
    <li ref={setNodeRef} style={style} data-testid={`candidate-card-${c.id}`} className={cn("plena-card plena-card-hover relative flex items-center gap-4 px-4 py-4 sm:px-5", isDragging && "z-10 shadow-plena-md ring-1 ring-plena-border")}>
      {sortable && (
        <button ref={setActivatorNodeRef} {...attributes} {...listeners} data-testid={`drag-handle-${c.id}`} aria-label={`${c.display_name} kartını sürükle`} className="hidden cursor-grab touch-none rounded-md p-1 text-plena-muted hover:text-plena-text active:cursor-grabbing sm:block"><GripVertical className="h-4 w-4" /></button>
      )}
      <span className="w-8 shrink-0 font-heading text-lg font-bold tabular-nums text-plena-muted" data-testid={`rank-number-${c.id}`}>{String(index + 1).padStart(2, "0")}</span>
      <button onClick={open} className="min-w-0 flex-1 text-left" data-testid={`candidate-open-${c.id}`}>
        <p className="truncate font-heading text-base font-semibold">{c.display_name}</p>
        <p className="truncate text-sm text-plena-text2">{[c.title, ...(c.meta || [])].filter(Boolean).join(" · ") || "CV"}</p>
      </button>
      <span className="hidden items-center gap-1.5 text-xs font-medium text-plena-text2 md:inline-flex" data-testid={`candidate-state-${c.id}`}>
        <StatusDot status={c.progress.status} /> {CANDIDATE_STATE[c.progress.status]}{c.progress.status === "IN_PROGRESS" && ` · ${c.progress.done}/${c.progress.total}`}
      </span>
      {sortable ? (
        <div className="flex items-center gap-2">
          <span className="w-14 text-right font-heading text-sm font-semibold text-plena-text2" data-testid={`card-overall-${c.id}`}>{overall != null ? `${overall}/100` : "—"}</span>
          <div className="flex flex-col">
          <Button variant="ghost" size="icon" className="h-7 w-7" disabled={index === 0} onClick={() => onMove(index, index - 1)} aria-label="Yukarı taşı" data-testid={`move-up-${c.id}`}><ChevronUp className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" disabled={index === total - 1} onClick={() => onMove(index, index + 1)} aria-label="Aşağı taşı" data-testid={`move-down-${c.id}`}><ChevronDown className="h-4 w-4" /></Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-1.5">
          <Button variant="ghost" size="sm" className="hidden rounded-full sm:inline-flex" onClick={open} data-testid={`candidate-cv-${c.id}`}><FileText className="mr-1.5 h-3.5 w-3.5" /> CV</Button>
          <Button size="sm" variant={c.progress.status === "COMPLETED" ? "outline" : "default"} className="btn-press rounded-full" onClick={open} data-testid={`candidate-evaluate-${c.id}`}>{c.progress.status === "COMPLETED" ? "Gözden Geçir" : "Değerlendir"} <ArrowRight className="ml-1 h-3.5 w-3.5" /></Button>
        </div>
      )}
    </li>
  );
}

export function CandidateBoard({ sortable = false }) {
  const { ctx, saveRanking } = useEvaluation();
  const ids = ctx.candidates.map((c) => c.id);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const move = (from, to) => saveRanking(arrayMove(ids, from, to)).catch(() => {});
  const onDragEnd = ({ active, over }) => { if (over && active.id !== over.id) move(ids.indexOf(active.id), ids.indexOf(over.id)); };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <ol className="space-y-3" data-testid="candidate-board">
          {ctx.candidates.map((c, i) => <CandidateCard key={c.id} c={c} index={i} total={ids.length} sortable={sortable} onMove={move} />)}
        </ol>
      </SortableContext>
    </DndContext>
  );
}
